import React, { useEffect, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { useCustomization } from 'common/contexts/Customization/useCustomization'
import { useDialog } from 'common/components/Dialog/hooks/useDialog'
import { Input } from 'common/components/Input/Input'
import { useFormRefsControl } from 'common/components/RefsControl/FormRefsControl/useFormRefsControl'
import { Switch } from 'common/components/Switch/Switch'
import { UpdateActions } from 'common/components/UpdateActions/UpdateActions'
import { useEventListener } from 'common/hooks/useEventListener'
import { getLandOrPortExThumb } from 'common/utils/exerciseUtils'
import { isVideoReady, VIDEO_PROCESSING_LABEL, VIDEO_PROCESSING_MESSAGE } from 'common/utils/videoUtils'
import { useListenExercisesQuery } from 'modules/ExerciseLibrary/exerciseLibraryApi'
import { useListenCoachVideosQuery } from 'modules/VideoLibrary/videoLibraryApi'
import {
  useListenProgramGroupsQuery,
  useListenProgramMultiWktDaysQuery,
  useListenProgramWorkoutsQuery,
} from 'modules/Layout/layoutApi'
import { useListenCollectionsQuery } from 'modules/Collections/collectionsApi'
import { useListenOrgProgramsListQuery } from 'modules/Programs/programApi'

import ButtonTypeInput from '../CommunityPostForm/ButtonTypeInput'
import SceneInput, { availableScenes } from '../CommunityPostForm/SceneInput'
import SuggestionInput from '../CommunityPostForm/SuggestionInput'
import { replyAttachmentSchema } from '../CommunityPostForm/schema'
import {
  getAvailableProgramWorkouts,
  getProgramWorkoutsLoading,
  getWorkoutDayLabel,
  shouldClearWorkoutId,
} from '../CommunityPostForm/workoutOptions'
import {
  buildReplyAttachmentFields,
  EXERCISE_UNAVAILABLE_MESSAGE,
  getAttachableExercises,
  getAttachableNamedItems,
  isUnattachableExercise,
  REPLY_BUTTON_TYPES,
} from '../postButtonUtils'

export const EMPTY_ATTACHMENT = {
  buttonType: '',
  link: '',
  openInDefaultBrowser: false,
  scene: '',
  videoId: '',
  programId: '',
  workoutId: '',
  exerciseId: '',
  collectionId: '',
  programGroupId: '',
}

export default function ReplyAttachmentForm({ postId, coachOrgId, initialAttachment, onAttach }) {
  const [, setDialogOpen] = useDialog()
  const { exVidOrientation } = useCustomization()
  const formId = `replyAttachmentForm-${postId}`

  const {
    watch,
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    setError,
    clearErrors,
  } = useForm({
    mode: 'onChange',
    defaultValues: { ...EMPTY_ATTACHMENT, ...initialAttachment },
    resolver: yupResolver(replyAttachmentSchema),
  })

  const formState = watch()
  const { buttonType } = formState

  const { data: videosData } = useListenCoachVideosQuery({ coachOrgId }, { skip: buttonType !== 'video' })
  const videos = videosData?.isLoading ? {} : videosData || {}
  const { data: programsData } = useListenOrgProgramsListQuery(
    { orgId: coachOrgId },
    { skip: buttonType !== 'program' && buttonType !== 'workout' }
  )
  const programs = programsData?.stillLoading ? {} : programsData || {}
  const { data: programWorkoutsData } = useListenProgramWorkoutsQuery(
    { programId: formState.programId },
    { skip: buttonType !== 'workout' || !formState.programId }
  )
  const programWorkoutsLoading = getProgramWorkoutsLoading({
    isWorkoutButton: buttonType === 'workout',
    programId: formState.programId,
    programWorkoutsData,
  })
  const { data: multiWktDaysData } = useListenProgramMultiWktDaysQuery(
    { programId: formState.programId },
    { skip: buttonType !== 'workout' || !formState.programId }
  )
  const programWorkouts = useMemo(
    () => getAvailableProgramWorkouts(programWorkoutsData, multiWktDaysData),
    [programWorkoutsData, multiWktDaysData]
  )
  const { data: exercisesData } = useListenExercisesQuery(
    { coachOrgId, exVidOrientation },
    { skip: buttonType !== 'exercise' }
  )
  const exercises = useMemo(() => getAttachableExercises(exercisesData), [exercisesData])
  const { data: collectionsData } = useListenCollectionsQuery({ coachOrgId }, { skip: buttonType !== 'collection' })
  const collections = useMemo(() => getAttachableNamedItems(collectionsData), [collectionsData])
  const { data: programGroupsData } = useListenProgramGroupsQuery(
    { coachOrgId },
    { skip: buttonType !== 'programGroup' }
  )
  const programGroups = useMemo(() => getAttachableNamedItems(programGroupsData), [programGroupsData])
  // Each list counts as loading until its query answers; `null` is an answer (nothing there).
  const videosLoading = videosData === undefined || videosData?.isLoading === true
  const programsLoading = programsData === undefined || programsData?.stillLoading === true
  const workoutsLoading =
    programWorkoutsLoading ||
    (Boolean(formState.programId) && (multiWktDaysData === undefined || multiWktDaysData?.isLoading === true))
  const exercisesLoading = exercisesData === undefined || exercisesData?.isFiller === true
  const collectionsLoading = collectionsData === undefined || collectionsData?.isLoading === true
  const programGroupsLoading = programGroupsData === undefined || programGroupsData?.isLoading === true

  const { addManyInputRefs, clearControlledRefs, moveFocusOnKeyPress } = useFormRefsControl()
  const buttonTypeRef = useRef()
  const sceneRef = useRef()
  const videoRef = useRef()
  const programRef = useRef()
  const workoutRef = useRef()
  const exerciseRef = useRef()
  const collectionRef = useRef()
  const programGroupRef = useRef()
  const linkRef = useRef()
  const submitRef = useRef()

  useEffect(() => {
    const typeRefs = {
      scene: [{ ref: sceneRef, name: 'scene' }],
      video: [{ ref: videoRef, name: 'video' }],
      program: [{ ref: programRef, name: 'program' }],
      workout: [
        { ref: programRef, name: 'program' },
        { ref: workoutRef, name: 'workout' },
      ],
      exercise: [{ ref: exerciseRef, name: 'exercise' }],
      collection: [{ ref: collectionRef, name: 'collection' }],
      programGroup: [{ ref: programGroupRef, name: 'programGroup' }],
      link: [{ ref: linkRef, name: 'link' }],
    }

    clearControlledRefs()
    addManyInputRefs([
      { ref: buttonTypeRef, name: 'buttonType' },
      ...(typeRefs[buttonType] || []),
      { ref: submitRef, name: 'submit' },
    ])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonType])

  useEffect(() => {
    if (
      shouldClearWorkoutId({
        buttonType,
        programId: formState.programId,
        workoutId: formState.workoutId,
        programWorkouts,
        programWorkoutsLoading,
      })
    ) {
      setValue('workoutId', '')
    }
  }, [buttonType, formState.programId, formState.workoutId, programWorkouts, programWorkoutsLoading, setValue])

  const onSubmit = (data) => {
    // The picker will not offer a video AWS is still transcoding. This is the check
    // behind it, as in the post form: members would get a card that cannot play.
    const selectedVideo = data.buttonType === 'video' ? videos?.[data.videoId] : null
    if (selectedVideo && !isVideoReady(selectedVideo)) {
      setError('videoId', { type: 'manual', message: VIDEO_PROCESSING_MESSAGE })
      return
    }
    if (data.buttonType === 'exercise' && isUnattachableExercise(exercisesData, data.exerciseId)) {
      setError('exerciseId', { type: 'manual', message: EXERCISE_UNAVAILABLE_MESSAGE })
      return
    }

    onAttach({
      draft: data,
      fields: buildReplyAttachmentFields({
        attachment: data,
        videos,
        programs,
        workouts: programWorkouts,
        exercises,
        collections,
        programGroups,
        exVidOrientation,
        scenes: availableScenes,
      }),
    })
    setDialogOpen(false)
  }

  useEventListener('keydown', (e) => moveFocusOnKeyPress(e, handleSubmit(onSubmit)))

  return (
    <>
      <div className='divide-y divide-gray-200 overflow-auto'>
        <form onSubmit={handleSubmit(onSubmit)} id={formId}>
          <div className='flex flex-col px-10 py-4'>
            <ButtonTypeInput
              inputRef={buttonTypeRef}
              value={buttonType}
              setValue={setValue}
              clearErrors={clearErrors}
              buttonTypes={REPLY_BUTTON_TYPES}
              label='Content type'
              ariaLabel='Content type'
              placeholder='Choose what to attach'
              error={errors?.buttonType}
            />
            {buttonType === 'scene' && (
              <SceneInput
                inputRef={sceneRef}
                value={formState.scene}
                setValue={setValue}
                coachOrgId={coachOrgId}
                error={errors?.scene}
              />
            )}
            {buttonType === 'video' && (
              <SuggestionInput
                inputRef={videoRef}
                label='Video'
                name='videoId'
                value={formState.videoId}
                selectedLabel={videos?.[formState.videoId]?.title || ''}
                suggestions={videos}
                setValue={setValue}
                clearErrors={clearErrors}
                error={errors?.videoId}
                getItemLabel={(video) => video?.title || ''}
                getPreviewImg={(video) => video?.previewImg}
                isItemDisabled={(video) => !isVideoReady(video)}
                disabledLabel={VIDEO_PROCESSING_LABEL}
                isLoading={videosLoading}
              />
            )}
            {(buttonType === 'program' || buttonType === 'workout') && (
              <SuggestionInput
                inputRef={programRef}
                label='Program'
                name='programId'
                value={formState.programId}
                selectedLabel={programs?.[formState.programId]?.name || ''}
                suggestions={programs}
                setValue={setValue}
                clearErrors={clearErrors}
                error={errors?.programId}
                getItemLabel={(program) => program?.name || ''}
                getPreviewImg={(program) => program?.previewImg}
                listAllOnFocus
                onSelect={() => {
                  if (buttonType === 'workout') {
                    setValue('workoutId', '', { shouldValidate: true })
                  }
                }}
                isLoading={programsLoading}
              />
            )}
            {buttonType === 'workout' && (
              <SuggestionInput
                inputRef={workoutRef}
                label='Workout'
                name='workoutId'
                value={formState.workoutId}
                selectedLabel={programWorkouts?.[formState.workoutId]?.title || ''}
                suggestions={programWorkouts}
                setValue={setValue}
                clearErrors={clearErrors}
                error={errors?.workoutId}
                getItemLabel={(workout) => workout?.title || ''}
                getPreviewImg={(workout) => workout?.previewImg}
                getItemSubtitle={getWorkoutDayLabel}
                listAllOnFocus
                isLoading={workoutsLoading}
              />
            )}
            {buttonType === 'exercise' && (
              <SuggestionInput
                inputRef={exerciseRef}
                label='Exercise'
                name='exerciseId'
                value={formState.exerciseId}
                selectedLabel={exercises?.[formState.exerciseId]?.name || ''}
                suggestions={exercises}
                setValue={setValue}
                clearErrors={clearErrors}
                error={errors?.exerciseId}
                getItemLabel={(exercise) => exercise?.name || ''}
                getPreviewImg={(exercise) => getLandOrPortExThumb(exVidOrientation, exercise)}
                isLoading={exercisesLoading}
              />
            )}
            {buttonType === 'collection' && (
              <SuggestionInput
                inputRef={collectionRef}
                label='Collection'
                name='collectionId'
                value={formState.collectionId}
                selectedLabel={collections?.[formState.collectionId]?.name || ''}
                suggestions={collections}
                setValue={setValue}
                clearErrors={clearErrors}
                error={errors?.collectionId}
                getItemLabel={(collection) => collection?.name || ''}
                getPreviewImg={(collection) => collection?.previewImg}
                listAllOnFocus
                isLoading={collectionsLoading}
              />
            )}
            {buttonType === 'programGroup' && (
              <SuggestionInput
                inputRef={programGroupRef}
                label='Series'
                name='programGroupId'
                value={formState.programGroupId}
                selectedLabel={programGroups?.[formState.programGroupId]?.name || ''}
                suggestions={programGroups}
                setValue={setValue}
                clearErrors={clearErrors}
                error={errors?.programGroupId}
                getItemLabel={(programGroup) => programGroup?.name || ''}
                getPreviewImg={(programGroup) => programGroup?.previewImg}
                placeholder='Search series'
                listAllOnFocus
                isLoading={programGroupsLoading}
              />
            )}
            {buttonType === 'link' && (
              <>
                <div className='mb-4'>
                  <label htmlFor='link' className='inline-flex cursor-pointer font-semibold text-tBlack mb-1'>
                    Link
                  </label>
                  <Input
                    name='link'
                    placeholder='https://...'
                    register={register}
                    inputRef={linkRef}
                    error={errors?.link?.message}
                  />
                  {errors?.link && <p className='flex text-xs mt-1 text-tRed'>{errors.link.message}</p>}
                </div>
                <div className='mb-4'>
                  <Switch
                    name='openInDefaultBrowser'
                    onChange={({ target: { checked } }) => setValue('openInDefaultBrowser', !checked)}
                    label='Link opens in-app'
                    isChecked={!formState.openInDefaultBrowser}
                    tooltipText='If your link opens another app, ie. YouTube, Instagram, Telegram, etc. disable this for best user experience'
                  />
                </div>
              </>
            )}
          </div>
        </form>
      </div>
      <UpdateActions handleSubmit={handleSubmit} actionText='Attach' form={formId} hideDelete={true} ref={submitRef} />
    </>
  )
}
